import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy } from '@angular/core';
import {
   AbstractControl,
   UntypedFormBuilder,
   UntypedFormControl,
   UntypedFormGroup,
   ValidationErrors,
   ValidatorFn,
   Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CallState } from '@lcs/shared/api';
import { select, Store } from '@ngrx/store';
import { ExtensionTypes } from 'generated/graphql-types';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AutoAttendantKeyItemView } from 'src/app/common-components/auto-attendant-key-list/models/auto-attendant-key-item.model';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { FormComponent } from 'src/app/common-components/forms-base/form-component.interface';
import { getAddExtensionCallState } from 'src/app/pages/add-extension/state/add-extension.selectors';
import { ExtensionDetailView } from 'src/app/pages/extension-details/models/extension-detail-view.model';
import { ExtensionTypeOption } from 'src/app/pages/extension-register/models/extension-type-option.interface';
import { AddAccountAutoAttendantKeysComponent } from '../../add-account-auto-attendant-keys/containers/add-account-auto-attendant-keys.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ndt-add-account-extension',
    templateUrl: './add-account-extension.component.html',
    standalone: false
})
export class AddAccountExtensionComponent implements FormComponent, OnDestroy {
   callState$: Observable<CallState>;

   form: UntypedFormGroup;

   extension: any;

   isEdit: boolean = false;

   extensionsToSave: ExtensionDetailView[] = [];

   newExtension: ExtensionDetailView = {
      extensionId: 0,
   };

   existingExtensions: ExtensionDetailView[];

   minTimeoutValue: number;

   maxTimeoutValue: number;

   private destroyed$ = new Subject<void>();

   constructor(
      public dialogRef: MatDialogRef<AddAccountExtensionComponent>,
      public dialogService: LcsDialogService,
      protected store: Store,
      @Inject(MAT_DIALOG_DATA) public data,
      private fb: UntypedFormBuilder,
      private changeDetectorRef: ChangeDetectorRef
   ) {
      this.callState$ = this.store.pipe(select(getAddExtensionCallState));
      this.extension = {
         ...data.extension,
      };
      if (data.extension?.number) {
         this.existingExtensions = data.existingExtensions.filter((ext) => ext.number !== data.extension.number);
         this.isEdit = true;
      } else {
         this.existingExtensions = data.existingExtensions;
      }
      this.newExtension.type = this.extension.type;
      this.form = this.fb.group({
         number: this.fb.control(
            this.extension?.number ??
               this.getNextNumberForExtensionType(
                  ExtensionTypes[this.newExtension.type?.name ?? 0],
                  this.existingExtensions
               ),
            [
               this.uniqueExtensionNumber(this.existingExtensions),
               Validators.min(100),
               Validators.max(99999),
               Validators.pattern(/^\d+$/),
               Validators.pattern(/^(?!911).*/),
               Validators.pattern(/^(?!933$)/),
               Validators.pattern(/^(?!(70[0-9]|7[1-9][0-9])$)/),
            ]
         ),
         name: this.fb.control(this.extension?.name),
         type: this.fb.control(this.newExtension.type, Validators.required),
         email: this.fb.control(this.extension?.email),
         timeout: this.fb.control(this.extension?.timeout),
         forwardTo: this.fb.control(this.extension?.forwardTo),
         autoAttendantKeys: this.fb.control(this.extension?.autoAttendantKeys ?? []),
      });

      this.form
         .get('type')
         ?.valueChanges.pipe(takeUntil(this.destroyed$))
         .subscribe((type: ExtensionTypeOption) => {
            if (type) {
               const newNumber = this.getNextNumberForExtensionType(ExtensionTypes[type.name], this.existingExtensions);
               this.form.get('number')?.setValue(newNumber);
               this.form.updateValueAndValidity();
            }
         });
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }
   onCancel() {
      this.dialogRef.close(this.data.existingExtensions);
   }

   onSetupKeys() {
      const data = {
         addedExtensions: this.existingExtensions,
         addedKeys: this.form.get('autoAttendantKeys')?.value,
      };
      const dialogRef = this.dialogService.openFormComponent(AddAccountAutoAttendantKeysComponent, {
         closeOnNavigation: true,
         data,
         disableClose: true,
         panelClass: ['add-dialog-panel', 'dialog-container'],
      });

      dialogRef.afterClosed().subscribe((autoAttendantKeys) => {
         if (autoAttendantKeys) {
            this.addAutoAttendantKeysToForm(autoAttendantKeys);
         }
      });
   }

   onSubmit(submitEvent: { extension: ExtensionDetailView; continuous?: boolean }) {
      if (this.form.valid) {
         this.extensionsToSave.push(submitEvent.extension);
         this.existingExtensions.push(submitEvent.extension);
         this.newExtension.type = submitEvent.extension.type;
         if (!submitEvent.continuous) {
            this.dialogRef.close(this.existingExtensions);
         } else {
            this.extension = this.newExtension;
            this.resetForm();
         }
      }
   }

   uniqueExtensionNumber(extensionsToSave: ExtensionDetailView[]): ValidatorFn {
      return (control: AbstractControl): ValidationErrors | null => {
         const numberControl = control as UntypedFormControl;
         if (extensionsToSave.filter((ext) => '' + ext.number === '' + control.value).length > 0) {
            return { unique: true };
         } else {
            return null;
         }
      };
   }

   getNextNumberForExtensionType(
      extensionType: ExtensionTypes,
      existingExtensions: ExtensionDetailView[] | null
   ): number | null {
      switch (extensionType) {
         case ExtensionTypes.AutoAttendant:
            if (existingExtensions) {
               const highestExtensionNumber = this.getHighestExtensionNumber(
                  existingExtensions.filter((ext) => ext.type?.name === ExtensionTypes.AutoAttendant)
               );
               return highestExtensionNumber ? +highestExtensionNumber + 1 : 500;
            } else {
               return 500;
            }
         case ExtensionTypes.HuntGroup:
            if (existingExtensions) {
               const highestExtensionNumber = this.getHighestExtensionNumber(
                  existingExtensions.filter((ext) => ext.type?.name === ExtensionTypes.HuntGroup)
               );
               return highestExtensionNumber ? +highestExtensionNumber + 1 : 200;
            } else {
               return 200;
            }
         case ExtensionTypes.Fax:
            if (existingExtensions) {
               const highestExtensionNumber = this.getHighestExtensionNumber(
                  existingExtensions.filter((ext) => ext.type?.name === ExtensionTypes.Fax)
               );
               return highestExtensionNumber ? +highestExtensionNumber + 1 : 300;
            } else {
               return 300;
            }
         case ExtensionTypes.VoiceMailbox:
            if (existingExtensions) {
               const highestExtensionNumber = this.getHighestExtensionNumber(
                  existingExtensions.filter((ext) => ext.type?.name === ExtensionTypes.VoiceMailbox)
               );
               return highestExtensionNumber ? +highestExtensionNumber + 1 : 400;
            } else {
               return 400;
            }
         case ExtensionTypes.ExternalForward:
            if (existingExtensions) {
               const highestExtensionNumber = this.getHighestExtensionNumber(
                  existingExtensions.filter((ext) => ext.type?.name === ExtensionTypes.ExternalForward)
               );
               return highestExtensionNumber ? +highestExtensionNumber + 1 : 800;
            } else {
               return 800;
            }
      }
      return null;
   }

   private getHighestExtensionNumber(extensions: ExtensionDetailView[]): number | null {
      if (extensions.length > 0) {
         return (
            extensions.sort((ext1, ext2) => {
               if (!ext1.number && !ext2.number) {
                  return 0;
               }

               if (!ext1.number || ext1.number > (ext2.number ?? 0)) {
                  return -1;
               }

               if (!ext2.number || ext1.number < ext2.number) {
                  return 1;
               }

               return 0;
            })[0].number ?? null
         );
      }
      return null;
   }

   private resetForm() {
      const newExtNumber = this.getNextNumberForExtensionType(
         ExtensionTypes[this.newExtension.type?.name ?? 0],
         this.existingExtensions
      );
      this.form.reset();
      this.form.get('number')?.setValue(newExtNumber);
      this.form.get('type')?.setValue(this.newExtension.type);
   }

   private addAutoAttendantKeysToForm(autoAttendantKeys: AutoAttendantKeyItemView[]) {
      if (this.form.get('autoAttendantKeys')) {
         this.form.get('autoAttendantKeys')?.setValue(autoAttendantKeys);
         this.form.get('autoAttendantKeys')?.updateValueAndValidity();
         this.changeDetectorRef.markForCheck();
      }
   }
}
